// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — the opponent.
//
// A deduction game against a weak opponent is not a deduction game, it is a slideshow, so this is a
// first-class deliverable and not a fallback. It plays from beliefs.ts — the same probabilities the
// player's own docket panel is drawn from — enumerates every legal move, and scores each one as
// expected standing rather than by a table of preferences.
//
// IT IS DETERMINISTIC given (public state, private view, seat, salt). That is not tidiness: in a
// networked hearing a peer whose player has gone quiet is played by this same function on every
// other peer at once, so two peers filling in for the same seat must produce the identical move or
// the room desyncs.
//
// It reads PublicState + PrivateView, exactly what a human at the table has. It cannot see the deck,
// it cannot see a hand it was not shown, and there is no difficulty tier that lets it.

import { makeRng } from '@ben-gy/game-engine/rng';
import { ADVOCATE, BAILIFF, CLERK, JUDGE, RECUSAL, RETRIAL, VERDICT, WITNESS } from './cards';
import {
  NAMEABLE, forcedCard, legalTargetsPub, needsTarget,
  type Card, type PrivateView, type PublicState,
} from './game';
import { beliefs, chanceAbove, chanceOf, entropy, medianRank, type Belief } from './beliefs';

export type TierName = 'clerk' | 'counsel' | 'silk' | 'blind';

export interface Tier {
  name: TierName;
  /** false = the control arm: no docket, no memory, every rank equally likely. */
  useDocket: boolean;
  /** Random noise added to every score, in standing-points. Higher = more mistakes. */
  noise: number;
  /** How much a rival's doubt is worth, relative to a kill. Information has no value at zero. */
  infoWeight: number;
  /** How much the bot cares about what it will be left holding when the docket runs dry. */
  keepWeight: number;
  /** Whether it aims at whoever is winning the MATCH rather than at whoever is convenient. */
  huntsLeader: boolean;
}

export const TIERS: Record<TierName, Tier> = {
  clerk: { name: 'clerk', useDocket: false, noise: 1.1, infoWeight: 0.1, keepWeight: 0.2, huntsLeader: false },
  counsel: { name: 'counsel', useDocket: true, noise: 0.35, infoWeight: 0.35, keepWeight: 0.5, huntsLeader: false },
  silk: { name: 'silk', useDocket: true, noise: 0, infoWeight: 0.5, keepWeight: 0.7, huntsLeader: true },
  /**
   * silk in every respect EXCEPT that it cannot deduce. The control arm for principle #18's negative
   * gap: if this does not lose decisively to silk, the deduction is decoration.
   */
  blind: { name: 'blind', useDocket: false, noise: 0, infoWeight: 0.5, keepWeight: 0.7, huntsLeader: true },
};

export const tierOf = (name: unknown): Tier =>
  typeof name === 'string' && Object.hasOwn(TIERS, name) ? TIERS[name as TierName] : TIERS.counsel;

export interface BotMove {
  cardId: number;
  target: string | null;
  named: number | null;
}

/** Being put out costs you the hearing. Everything else is priced against this. */
const KILL = 3;
/** …and putting yourself out costs the same, which is why the Verdict is never played by choice. */
const SUICIDE = -6;

/**
 * What a card is worth to be caught HOLDING when the docket runs dry.
 *
 * Not the rank: the Verdict wins every showdown and is also the only card that can lose you the
 * hearing outright, and the Judge is inert but beats everything below it.
 */
const holdValue = (rank: number): number => (rank === VERDICT ? 2.6 : rank / 4);

export function choose(
  p: PublicState,
  priv: PrivateView,
  id: string,
  tier: Tier,
  salt: number,
): BotMove | null {
  if (p.phase !== 'play' || p.turn !== id) return null;
  const hand = priv.hand;
  if (hand.length === 0) return null;

  const myRanks = hand.map((c) => c.rank);
  const bel = beliefs(p, id, myRanks, priv.learned, { useDocket: tier.useDocket });
  // Keyed on PUBLIC values only. Two peers filling in for the same silent seat must compute the
  // identical move, and round/ply/seat are identical on every peer — while the deal seed, which
  // would also have worked, is host-private precisely so that a guest cannot re-deal the hearing.
  const rng = makeRng(`${p.modeId}:${p.round}:${p.ply}:${id}:${salt}`);

  const forced = forcedCard(hand);
  const options = forced ? [forced] : hand;

  let best: BotMove | null = null;
  let bestScore = -Infinity;

  for (const card of options) {
    const keep = hand.filter((c) => c.id !== card.id);
    const keepScore = tier.keepWeight * keep.reduce((a, c) => a + holdValue(c.rank), 0);
    const targets = legalTargetsPub(p, id, card.rank);

    if (!needsTarget(card.rank) || targets.length === 0) {
      const s = inertScore(card, keep, p, id) + keepScore + rng() * tier.noise;
      if (s > bestScore) {
        bestScore = s;
        best = { cardId: card.id, target: null, named: null };
      }
      continue;
    }

    for (const t of targets) {
      const b = bel[t] ?? [];
      const threat = threatOf(p, t, id, tier);
      if (card.rank === BAILIFF) {
        for (const named of NAMEABLE) {
          const hit = chanceOf(b, named);
          // A miss is not a wasted turn any more: it buys a private halving of their doubt. Naming
          // the median maximises that, naming a likely card maximises the kill, and the bot weighs
          // the two rather than always reaching for one.
          const info = tier.infoWeight * splitValue(b, named);
          const s = hit * KILL * threat + (1 - hit) * info + keepScore + rng() * tier.noise;
          if (s > bestScore) {
            bestScore = s;
            best = { cardId: card.id, target: t, named };
          }
        }
        continue;
      }
      let s = keepScore + rng() * tier.noise;
      switch (card.rank) {
        case CLERK:
          s += tier.infoWeight * entropy(b) * threat;
          break;
        case WITNESS: {
          const mine = Math.max(...keep.map((c) => c.rank), 0);
          const win = 1 - chanceAbove(b, mine - 1);
          const lose = chanceAbove(b, mine);
          s += win * KILL * threat + lose * SUICIDE;
          break;
        }
        case RETRIAL: {
          if (t === id) {
            // Only worth it to shed something poor, and never while holding the Verdict.
            const mine = Math.max(...keep.map((c) => c.rank), 0);
            s += keep.some((c) => c.rank === VERDICT) ? SUICIDE : (3 - mine) * 0.6;
          } else {
            s += chanceOf(b, VERDICT) * KILL * threat + 0.35 * tier.infoWeight * threat;
          }
          break;
        }
        case ADVOCATE: {
          const mine = keep.reduce((a, c) => a + holdValue(c.rank), 0);
          const theirs = expectedHold(b) * Math.max(1, keep.length);
          s += (theirs - mine) * 0.8 + tier.infoWeight * 0.6;
          break;
        }
        default:
          break;
      }
      if (s > bestScore) {
        bestScore = s;
        best = { cardId: card.id, target: t, named: null };
      }
    }
  }
  return best;
}

/** A card with nobody to point at, or no power at all. */
function inertScore(card: Card, keep: readonly Card[], p: PublicState, id: string): number {
  const rivals = p.seats.filter((x) => x.alive && x.id !== id).length;
  switch (card.rank) {
    case RECUSAL:
      // A shield is worth exactly as much as the number of people who could be aiming at you, and
      // more still when what you are protecting is the Verdict.
      return 0.55 * rivals + (keep.some((c) => c.rank === VERDICT) ? 1.4 : 0);
    case VERDICT:
      return SUICIDE;
    case JUDGE:
      return 0.15;
    default:
      // A targeted card with no legal target is a wasted turn, but it is also a free way to shed a
      // card you did not want — so it is mildly good, never great.
      return 0.1;
  }
}

/** Expected hold-value of an unknown card drawn against this belief. */
function expectedHold(b: Belief): number {
  let v = 0;
  for (let r = 1; r <= 8; r++) v += (b[r] ?? 0) * holdValue(r);
  return v;
}

/**
 * How much a given rival is worth spending a card on.
 *
 * Everyone is worth 1. A tier that hunts the leader adds up to a half again for whoever is closest
 * to the target, which is the difference between a bot that plays each hearing in isolation and one
 * that plays the match.
 */
function threatOf(p: PublicState, target: string, me: string, tier: Tier): number {
  if (!tier.huntsLeader) return 1;
  const mine = p.standing[me] ?? 0;
  const theirs = p.standing[target] ?? 0;
  const lead = Math.max(0, theirs - mine);
  return 1 + Math.min(0.5, lead / Math.max(1, p.target));
}

/**
 * The information a MISSED call buys, in bits, for naming `rank`.
 *
 * The hint splits their remaining doubt into "above" and "below"; the closer that split is to even,
 * the more a miss tells you. This is what turns five Bailiffs in a sixteen-card deck from a lottery
 * into a binary search, and it is the single measurable reason this game is not a coin flip.
 */
export function splitValue(b: Belief, rank: number): number {
  const above = chanceAbove(b, rank);
  const hit = chanceOf(b, rank);
  const below = Math.max(0, 1 - above - hit);
  const miss = above + below;
  if (miss <= 0) return 0;
  const pa = above / miss;
  const pb = below / miss;
  let h = 0;
  if (pa > 0) h -= pa * Math.log2(pa);
  if (pb > 0) h -= pb * Math.log2(pb);
  return h;
}

/** Which rank a caller would name if it wanted information above all. Exposed for the bot tests. */
export const infoNamed = (b: Belief): number => medianRank(b);

/**
 * Who to back from the gallery once you are out.
 *
 * Whoever is most likely to still be standing: the strongest testimony you can still account for,
 * broken by who has already survived longest. It uses the same beliefs the hand did, so a player who
 * was reading the table well is rewarded for it even after they have been put out.
 */
export function chooseBack(p: PublicState, priv: PrivateView, id: string, tier: Tier): string | null {
  const alive = p.seats.filter((x) => x.alive && x.id !== id);
  if (alive.length < 2) return null;
  const bel = beliefs(p, id, priv.hand.map((c) => c.rank), priv.learned, { useDocket: tier.useDocket });
  let best: string | null = null;
  let bestScore = -Infinity;
  for (const seat of alive) {
    const b = bel[seat.id] ?? [];
    let strength = 0;
    for (let r = 1; r <= 8; r++) strength += (b[r] ?? 0) * r;
    // A shield now is a shield through the next turn, which is a real edge on surviving to the close.
    const s = strength + (seat.shielded ? 0.8 : 0) + seat.discards.length * 0.05;
    if (s > bestScore) {
      bestScore = s;
      best = seat.id;
    }
  }
  return best;
}
